import React, { useEffect, useState } from "react";
import AppointmentTypeSelectionCard from "./AppointmentTypeSelectionCard";
import { DropOffIcon, ExpressServiceIcon, UrgentVisitIcon } from "../Icons";
import { CREATE_BOOKING_TYPE, MODALS } from "../../config/params";
import { useModal } from "../../context/ModalContext";
import { useAllServiceStatusStore, useBookingAppointmentTypeStore } from "../../hooks";
import { useAppointmentStore } from "../../hooks/redux-hooks/appointment-store/appointment.store";
import { useOverdueInvoiceStore } from "../../hooks/redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { usePetList } from "../../services/pet-list/pet-list.service";
import { usePetStore } from "../../hooks/redux-hooks/pet-store/pet-store.service";
import { userActivityLogger } from "../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";

const AppointmentTypeSelectionList = () => {
  const [selectedBookingType, setSelectedBookingType]= useState<string>("");
  const { openModal } = useModal();
  const { setSelectedPets, setAppointmentsForm, selectedPets } = useAppointmentStore();
  const { setBookingAppointmentType } = useBookingAppointmentTypeStore();
  const { petsActive }=usePetList();
  const { pendingPaymentPetList, isBookingBlocked, pausedWellnessPetList, cancelledWellnessPetList } =
  useOverdueInvoiceStore();
  const { isDropOffServiceAvailable, isExpressServiceAvailable }=useAllServiceStatusStore();
  const { petList }=usePetStore();


  const handleCardSelection = (appointmentType: string)=>{
    setSelectedBookingType(appointmentType);
  };

  const handleExpressEligibilityCheck = ()=>{
    const eligiblePets = selectedPets.filter((pet)=> pet.isEligibleForExpressVisit);

    if (eligiblePets.length !== selectedPets.length) {
      setSelectedPets([]);
    }
  };

  const checkExpressAvailability = ()=>{
    if (petList) {
      const expressEligiblePets = petList.filter((pet) => pet.isEligibleForExpressVisit);

      return expressEligiblePets.length > 0;
    }
  };

  const handleUrgentVisitSelection = () => {
    setAppointmentsForm(null);
    setBookingAppointmentType(CREATE_BOOKING_TYPE.URGENT_BOOKING);
    if (!isBookingBlocked && (pendingPaymentPetList.length > 0)) {
      if (cancelledWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
      } else if (pausedWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL);
      } else {
        openModal(MODALS.GENERIC_PAYMENT_ISSUE_MODAL);
      }
    } else {
      openModal(MODALS.URGENT_VISIT_MODAL);
    }
  };

  const handleDropoffSelection = () => {
    setAppointmentsForm(null);
    setBookingAppointmentType(CREATE_BOOKING_TYPE.DROPOFF_BOOKING);
    if (petsActive?.length==1) {
      setSelectedPets(petsActive);
    }
    if (!isBookingBlocked && (pendingPaymentPetList.length > 0)) {
      if (cancelledWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
      } else if (pausedWellnessPetList.length > 0) {
        openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL);
      } else {
        openModal(MODALS.GENERIC_PAYMENT_ISSUE_MODAL);
      }
    } else {
      openModal(MODALS.UPDATED_BOOK_A_VISIT_MODAL);
    }
  };

  useEffect(()=>{
    switch (selectedBookingType) {
    case "Urgent Visit":
      handleUrgentVisitSelection();
      setSelectedBookingType("");
      break;
    case "Drop-Off":
      handleDropoffSelection();
      setSelectedBookingType("");
      break;
    case "Express Services":
      userActivityLogger(MIXPANEL_EVENTS.EXPRESS_SERVICE_SELECTED, {
        appointment_category: "tech", referred_from_page: "dashboard",
      });
      handleExpressEligibilityCheck();
      setBookingAppointmentType(CREATE_BOOKING_TYPE.EXPRESS_BOOKING);
      if (!isBookingBlocked && (pendingPaymentPetList.length > 0)) {
        if (cancelledWellnessPetList.length > 0) {
          openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
        } else if (pausedWellnessPetList.length > 0) {
          openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL);
        } else {
          openModal(MODALS.GENERIC_PAYMENT_ISSUE_MODAL);
        }
      } else {
        openModal(MODALS.EXPRESS_VISIT_MODAL);
      }
      setSelectedBookingType("");
      break;
    default:
      setSelectedBookingType("");
      break;
    }
  }, [selectedBookingType]);

  return (
    <div className="flex justify-between gap-3 w-full">
      {isDropOffServiceAvailable &&
      <AppointmentTypeSelectionCard
        isSelected={selectedBookingType==="Drop-Off"}
        icon={<DropOffIcon />}
        disabled={!isDropOffServiceAvailable}
        cardTitle="Drop-Off"
        onClick={handleCardSelection}
      />}
      {checkExpressAvailability() && isExpressServiceAvailable &&
      <AppointmentTypeSelectionCard
        isSelected={selectedBookingType==="Express Services"}
        icon={<ExpressServiceIcon />}
        cardTitle="Express Services"
        onClick={handleCardSelection}
      />}
      <AppointmentTypeSelectionCard
        isSelected={selectedBookingType==="Urgent Visit"}
        icon={<UrgentVisitIcon />}
        cardTitle="Urgent Visit"
        onClick={handleCardSelection}
      />
    </div>
  );
};

export default AppointmentTypeSelectionList;
